import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Menu,
  Select,
  Grid,
  Paper,
  ThemeIcon,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconArrowBackUp,
  IconCheck,
  IconPackageImport,
  IconPackageExport,
  IconShieldCheck,
  IconUser,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { repositoryMdm, repositoryPos } from "../../_base/_const/_constVar";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { _localization } from "../../config/location";
import { green } from "../../const/variables";
import { MessageResponse } from "../../model/MessageResponse";
import { mapDetailToVoucherInfo } from "./serviceRepairVoucherDetail";

// Helper lấy giá trị từ mảng theo id
const getValueById = (id: string, data: any[], field: string) => {
  const item = data?.find((x) => x.value === id);
  if (item) return item[field] || item.label;
  return null;
};

// Ô nhập Serial mới: giữ state cục bộ để gõ + uppercase mượt,
// chỉ commit vào data của bảng khi blur -> tránh re-render toàn bảng mỗi ký tự.
const SerialInput = React.memo(function SerialInput({
  value,
  onCommit,
  error,
  readOnly,
}: {
  value: string;
  onCommit: (val: string) => void;
  error?: boolean | string;
  readOnly?: boolean;
}) {
  const [resetKey, setResetKey] = useState(value ?? "");

  useEffect(() => {
    setResetKey(value ?? "");
  }, [value]);

  return (
    <TextInput
      key={resetKey}
      placeholder="Serial nhập từ NCC"
      defaultValue={value ?? ""}
      styles={{ input: { textTransform: "uppercase" } }}
      error={error}
      readOnly={readOnly}
      variant={readOnly ? "filled" : "default"}
      onBlur={(e) => {
        if (readOnly) return;
        const newVal = e.currentTarget.value.toUpperCase();
        if (newVal !== (value ?? "")) {
          onCommit(newVal);
        }
      }}
    />
  );
});

// Ô nhập text chung (Ghi chú): tránh re-render toàn bảng mỗi ký tự
const LocalTextInput = React.memo(function LocalTextInput({
  value,
  onCommit,
  placeholder,
  error,
  readOnly,
}: {
  value: string;
  onCommit: (val: string) => void;
  placeholder?: string;
  error?: boolean | string;
  readOnly?: boolean;
}) {
  const [resetKey, setResetKey] = useState(value ?? "");

  useEffect(() => {
    setResetKey(value ?? "");
  }, [value]);

  return (
    <TextInput
      key={resetKey}
      placeholder={placeholder}
      defaultValue={value ?? ""}
      error={error}
      readOnly={readOnly}
      variant={readOnly ? "filled" : "default"}
      onBlur={(e) => {
        if (readOnly) return;
        const newVal = e.currentTarget.value;
        if (newVal !== (value ?? "")) {
          onCommit(newVal);
        }
      }}
    />
  );
});

const ServiceRepairInWardWarrantyVendor = (props: {
  back: any;
  dataRowProps: any;
  setDataRowProps: any;
  handleWarrantyInward?: any;
  typeView: string;
  dataVendorSelect?: any;
  setTblItemTransactionHeaderCommand?: any;
  inspectorId?: string | null;
  setInspectorId?: any;
  inspectorNote?: string | null;
  setInspectorNote?: any;
  /** CREATE: lập phiếu mới. DETAIL: xem lại phiếu đã lưu, dùng chung layout, khoá nhập liệu. */
  mode?: "CREATE" | "DETAIL";
  /** Dữ liệu trả về từ API chi tiết, bắt buộc khi mode = DETAIL */
  detail?: any;
}) => {
  const {
    back,
    dataRowProps,
    setDataRowProps,
    handleWarrantyInward,
    typeView,
    dataVendorSelect,
    setTblItemTransactionHeaderCommand,
    inspectorId,
    setInspectorId,
    inspectorNote,
    setInspectorNote,
    mode = "CREATE",
    detail,
  } = props;
  const isDetail = mode === "DETAIL";
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [data, setData] = useState<any>(dataRowProps);
  const [dataProduct, setDataProduct] = useState<any[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);

  useEffect(() => {
    if (isDetail) return;
    if (
      dataRowProps &&
      dataRowProps.length > 0 &&
      dataRowProps[0].attribute27 &&
      dataVendorSelect
    ) {
      const vendorId = dataRowProps[0].attribute27.toString();
      setSelectedVendorId(vendorId);
      if (setTblItemTransactionHeaderCommand) {
        setTblItemTransactionHeaderCommand((prev: any) => ({
          ...prev,
          // attribute15 = MÃ NCC, lưu vào header phiếu nhập để màn xem chi tiết đọc được NCC
          // (dataVendorSelect dùng value = vendorCode nên selectedVendorId chính là mã NCC)
          attribute15: vendorId,
          customerId: Number(vendorId),
          customerName: getValueById(vendorId, dataVendorSelect, "label"),
          telephoneNumber: getValueById(vendorId, dataVendorSelect, "phone"),
          toAddress: getValueById(vendorId, dataVendorSelect, "addr"),
        }));
      }
    }
  }, [dataRowProps, dataVendorSelect]);

  const [visible, { toggle, close, open }] = useDisclosure(false);
  const [isSaved, setIsSaved] = useState(false);
  const [inwardInfo, setInwardInfo] = useState<any>(null);

  const getInwardInfo = async () => {
    try {
      const res = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/Inward/get-service-vendor-warranty-inward`,
      );
      if (res?.success) {
        setInwardInfo(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // Ở chế độ xem chi tiết, mã phiếu / người lập lấy từ phiếu đã lưu
    // chứ không sinh mã mới.
    if (isDetail) return;
    getInwardInfo();
  }, [isDetail]);

  // Đổ dữ liệu phiếu đã lưu vào đúng các ô của màn tạo mới
  useEffect(() => {
    if (!isDetail || !detail) return;

    setInwardInfo(mapDetailToVoucherInfo(detail));
    // Danh mục NCC ở màn này dùng mã NCC làm value
    setSelectedVendorId(detail.vendorCode ?? null);
  }, [isDetail, detail]);

  // Ở chế độ chi tiết, người kiểm tra và ghi chú KTKT lấy từ phiếu KTKT đã sinh ra
  const displayInspectorId = isDetail
    ? detail?.inspectorId != null
      ? detail.inspectorId.toString()
      : null
    : inspectorId;
  const displayInspectorNote = isDetail
    ? (detail?.inspectorNote ?? "")
    : inspectorNote;
  const displayDescription = isDetail ? (detail?.description ?? "") : undefined;

  const [dataEmployee, setDataEmployee] = useState<any[]>([]);

  const fetchEmployees = async () => {
    try {
      const res = await repositoryPos.get<MessageResponse<any[]>>(
        "/api/v1/TblDmEmployee/get-list?Take=1000&Status=A",
      );
      if (res?.success && res.data) {
        setDataEmployee(
          res.data.map((emp: any) => ({
            value: emp.id.toString(),
            label: `${emp.code} - ${emp.fullname}`,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const userLogin =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("userLogin") || "{}")
      : {};

  useEffect(() => {
    if (!inspectorId && userLogin?.id && setInspectorId) {
      setInspectorId(userLogin.id.toString());
    }
  }, [userLogin?.id, inspectorId, setInspectorId]);

  const firstItem = data && data.length > 0 ? data[0] : null;

  const confirmChangeSerial = () => {
    return data.every((item: any) => item.formChange === "KHONG_DOI");
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "owWarrantyCode",
        header: "Số phiếu gửi NCC",
        Cell: ({ renderedCellValue }) => {
          if (!renderedCellValue) return null;
          return <Text size="12.5px">{renderedCellValue}</Text>;
        },
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "itemCode",
        header: "Thông tin sản phẩm",
        Cell: ({ row }) => (
          <Box w={250}>
            <Text size="12.5px" c="black">
              {row.original.itemCode}
            </Text>
            <Tooltip label={row.original.itemName} multiline w={250}>
              <Text size="12px" truncate="end" c="dimmed">
                {row.original.itemName}
              </Text>
            </Tooltip>
          </Box>
        ),
        size: 300,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "serial",
        header: "Serial",
        Cell: ({ row }) => (
          <TextInput
            variant="unstyled"
            defaultValue={row.original.serial}
            readOnly
          />
        ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "note",
        header: "Ghi chú",
        Cell: ({ row }) => (
          <LocalTextInput
            placeholder="Ghi chú"
            value={row.original.note ?? ""}
            readOnly={isDetail}
            onCommit={(val) =>
              handleChange(row.original.serial?.toString(), {
                note: val,
              })
            }
          />
        ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        size: 300,
      },
      {
        accessorKey: "formChange",
        header: "Hình thức đổi",
        Cell: ({ row }) => (
          <Select
            placeholder="Hình thức đổi trả"
            disabled={isDetail}
            data={[
              { label: "Không đổi", value: "KHONG_DOI" },
              { label: "Chỉ đổi serial", value: "DOI_TUONG_DUONG" },
              { label: "Đổi mã sp và serial", value: "DOI_MOI" },
            ]}
            value={row.original.formChange}
            onChange={(e) => {
              const changeType = e || "KHONG_DOI";
              let defaultUpdates: any = {
                formChange: changeType,
                attribute5: null,
                parentSerial: null,
              };

              if (changeType === "DOI_TUONG_DUONG") {
                defaultUpdates.attribute4 = row.original.itemId;
                defaultUpdates.newItemCode = row.original.itemCode;
                defaultUpdates.newItemName = row.original.itemName;
              } else {
                defaultUpdates.attribute4 = null;
                defaultUpdates.newItemCode = "";
                defaultUpdates.newItemName = "";
              }

              handleChange(row.original.serial?.toString(), defaultUpdates);
            }}
          />
        ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "newItemCode",
        header: "Thông tin sản phẩm mới",
        Cell: ({ row }) => {
          return row.original.formChange === "DOI_MOI" ? (
            <Select
              placeholder="Mã sản phẩm mới"
              searchable
              clearable
              disabled={isDetail}
              error={
                isDetail
                  ? false
                  : !row.original.attribute4 ||
                    row.original.attribute4.toString().trim() === ""
              }
              value={row.original.attribute4?.toString() || null}
              data={[
                ...(dataProduct?.map((item: any) => ({
                  value: item.itemId?.toString(),
                  label: `${item.itemCode} - ${item.itemName}`,
                })) || []),
                ...(row.original.attribute4 &&
                !dataProduct?.some(
                  (i: any) =>
                    i.itemId?.toString() ===
                    row.original.attribute4?.toString(),
                )
                  ? [
                      {
                        value: row.original.attribute4?.toString(),
                        label: `${row.original.newItemCode} - ${row.original.newItemName || ""}`,
                      },
                    ]
                  : []),
              ]}
              onSearchChange={(query) => {
                if (query.length > 2) {
                  let actualQuery = query;
                  if (query.includes(" - ")) {
                    actualQuery = query.split(" - ")[0];
                  }
                  getDataItem(actualQuery, row.original.serial);
                }
              }}
              onChange={(value) => {
                if (value) {
                  const selectedItem = dataProduct.find(
                    (i: any) => i.itemId?.toString() === value,
                  );
                  if (selectedItem) {
                    handleChange(row.original.serial?.toString(), {
                      attribute4: selectedItem.itemId,
                      newItemCode: selectedItem.itemCode,
                      newItemName: selectedItem.itemName,
                    });
                  }
                } else {
                  handleChange(row.original.serial?.toString(), {
                    attribute4: null,
                    newItemCode: "",
                    newItemName: "",
                  });
                }
              }}
              nothingFoundMessage="Không tìm thấy sản phẩm"
              onClick={() => {
                if (!dataProduct || dataProduct.length === 0) {
                  getDataItem("");
                }
              }}
            />
          ) : null;
        },
        size: 300,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "attribute5",
        header: "Serial mới",
        Cell: ({ row }) =>
          row.original.formChange !== "DOI_TUONG_DUONG" &&
          row.original.formChange !== "DOI_MOI" ? null : (
            <SerialInput
              value={row.original.attribute5 ?? ""}
              readOnly={isDetail}
              error={
                isDetail
                  ? false
                  : !row.original.attribute5 ||
                    row.original.attribute5.toString().trim() === ""
              }
              onCommit={(val) =>
                handleChange(row.original.serial?.toString(), {
                  attribute5: val,
                })
              }
            />
          ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "parentSerial",
        header: "Serial con",
        Cell: ({ row }) =>
          row.original.formChange !== "DOI_TUONG_DUONG" &&
          row.original.formChange !== "DOI_MOI" ? null : (
            <SerialInput
              value={row.original.parentSerial ?? ""}
              readOnly={isDetail}
              onCommit={(val) =>
                handleChange(row.original.serial?.toString(), {
                  parentSerial: val,
                })
              }
            />
          ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "postionCode",
        header: "Vị trí nhập",
        Cell: ({ row }) =>
          row.original.postionCode && row.original.positionname
            ? `${row.original.postionCode} - ${row.original.positionname}`
            : row.original.postionCode || "",
        size: 150,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
    ],
    [data, dataProduct],
  );

  const handleChange = (serial: string, updates: Record<string, any>) => {
    const updatedData = data.map((item: any) =>
      item.serial?.toString() === serial?.toString()
        ? {
            ...item,
            ...updates,
          }
        : item,
    );
    setData(updatedData);
    setDataRowProps(updatedData);
  };

  const handleConfirmInward = async () => {
    const changedItems = data.filter(
      (item: any) =>
        item.formChange === "DOI_MOI" || item.formChange === "DOI_TUONG_DUONG",
    );

    // BHMR: gói đang kích hoạt trên serial cũ sẽ chuyển sang serial mới khi lưu phiếu.
    // Tra bằng scan-serial (trả bhmrPackages); không tra được thì bỏ qua, popup vẫn mở như cũ
    const bhmrBySerial: Record<
      string,
      { cardItemCode: string; cardSerial: string }[]
    > = {};
    const oldSerials: string[] = Array.from(
      new Set(
        changedItems
          .map((item: any) => String(item.serial ?? "").trim())
          .filter((serial: string) => serial.length > 0),
      ),
    );
    open();
    try {
      await Promise.all(
        oldSerials.map(async (serial) => {
          try {
            const res: any = await repositoryPos.get(
              `/api/v1/TblWarrantyItem/scan-serial?Serial=${encodeURIComponent(serial)}`,
            );
            const packages = res?.success ? res.data?.bhmrPackages : null;
            if (packages?.length) bhmrBySerial[serial.toUpperCase()] = packages;
          } catch {
            // không tra được gói của serial này
          }
        }),
      );
    } finally {
      close();
    }
    const packagesOf = (item: any) =>
      bhmrBySerial[String(item.serial ?? "").trim().toUpperCase()] ?? [];
    const countBhmr = changedItems.filter(
      (item: any) => packagesOf(item).length > 0,
    ).length;

    modals.openConfirmModal({
      title: "Xác nhận nhập kho bảo hành NCC",
      centered: true,
      size: changedItems.length > 0 ? "lg" : undefined,
      children: (
        <div style={{ paddingBottom: 24, paddingTop: 5 }}>
          <Text size="lg" fw={600} ta="center" mb={8}>
            Bạn có chắc chắn các thông tin đã nhập là chính xác?
          </Text>

          {changedItems.length > 0 && (
            <Box
              my="md"
              p="sm"
              style={{ border: "1px solid #e2e8f0", borderRadius: 8 }}
            >
              <Group justify="space-between" align="center" mb="xs" gap="md" wrap="nowrap">
                <Text fw={600} c="blue" size="sm">
                  Danh sách sản phẩm thay đổi:
                </Text>
                {countBhmr > 0 && (
                  <Badge color="teal" variant="light" size="sm" radius="sm">
                    Chuyển gói BHMR: {countBhmr}
                  </Badge>
                )}
              </Group>
              <Table withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Sản phẩm cũ (Serial)</Table.Th>
                    <Table.Th>Hình thức</Table.Th>
                    <Table.Th>Sản phẩm mới (Serial)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {changedItems.map((item: any, idx: number) => (
                    <Table.Tr key={idx}>
                      <Table.Td>
                        {item.itemCode}
                        <br />
                        <Text size="xs" c="dimmed">
                          SN: {item.serial}
                        </Text>
                        {packagesOf(item).map((pkg) => (
                          <Group key={pkg.cardSerial} gap={4} mt={4} wrap="nowrap">
                            <IconShieldCheck size={14} color="var(--mantine-color-teal-7)" />
                            <Text size="xs" c="dimmed">
                              Gói BHMR {pkg.cardItemCode} (thẻ {pkg.cardSerial})
                            </Text>
                          </Group>
                        ))}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            item.formChange === "DOI_MOI" ? "red" : "orange"
                          }
                          variant="light"
                          size="sm"
                        >
                          {item.formChange === "DOI_MOI"
                            ? "Đổi mới"
                            : "Đổi tương đương"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {item.newItemCode || item.itemCode}
                        <br />
                        <Text size="xs" c="dimmed">
                          SN: {item.attribute5}
                        </Text>
                        {packagesOf(item).map((pkg) => (
                          <Group key={pkg.cardSerial} gap={4} mt={4} wrap="nowrap">
                            <IconShieldCheck size={14} color="var(--mantine-color-teal-7)" />
                            <Text size="xs" fw={500} c="teal.7">
                              Nhận gói BHMR {pkg.cardItemCode}
                            </Text>
                          </Group>
                        ))}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}

          <Text
            component="div"
            ta="center"
            size="sm"
            fs="italic"
            c="red"
            fw={500}
          >
            *Lưu ý: Phiếu nhập sẽ không thể sửa khi ấn "Đồng ý".
          </Text>

          {changedItems.length > 0 && (
            <Text
              component="div"
              ta="center"
              size="sm"
              fs="italic"
              c="yellow.8"
              fw={500}
              mt={5}
            >
              *Lưu ý cần làm đổi mã ở trên hệ thống Asia (nếu có)
            </Text>
          )}

          {countBhmr > 0 && (
            <Text
              component="div"
              ta="center"
              size="sm"
              fs="italic"
              c="teal.7"
              fw={500}
              mt={5}
            >
              *Gói BHMR đang kích hoạt trên serial cũ sẽ chuyển sang serial mới,
              giữ nguyên ngày kích hoạt.
            </Text>
          )}
        </div>
      ),
      labels: { confirm: "Đồng ý", cancel: "Hủy" },
      onConfirm: async () => {
        open();
        try {
          const success = await handleWarrantyInward();
          if (success) setIsSaved(true);
        } finally {
          close();
        }
      },
    });
  };

  const handleSave = () => {
    // 0. Kiểm tra Người kiểm tra bắt buộc
    if (!inspectorId) {
      NotificationExtension.Fails("Vui lòng chọn Người kiểm tra !");
      return;
    }
    // 1. Kiểm tra DOI_TUONG_DUONG -> Serial mới required
    const missingSerialTuongDuong = data.filter(
      (i: any) =>
        i.formChange === "DOI_TUONG_DUONG" &&
        (!i.attribute5 || i.attribute5.toString().trim().length === 0),
    );
    if (missingSerialTuongDuong.length > 0) {
      NotificationExtension.Fails(
        `Vui lòng nhập Serial mới cho các serial: ${missingSerialTuongDuong.map((i: any) => i.serial).join(", ")} !`,
      );
      return;
    }

    // 2. Kiểm tra DOI_MOI -> Serial mới và Mã SP mới required
    const missingMoi = data.filter(
      (i: any) =>
        i.formChange === "DOI_MOI" &&
        (!i.attribute4 ||
          !i.attribute5 ||
          i.attribute5.toString().trim().length === 0),
    );
    if (missingMoi.length > 0) {
      NotificationExtension.Fails(
        `Vui lòng chọn Mã sản phẩm mới và nhập Serial mới đầy đủ cho các serial: ${missingMoi.map((i: any) => i.serial).join(", ")} !`,
      );
      return;
    }

    // 3. Kiểm tra nếu hình thức đổi là đổi mới và đổi tương đương
    const dataErrorDtd = data.filter(
      (i: any) =>
        i.formChange === "DOI_TUONG_DUONG" &&
        String(i.serial) === String(i.attribute5),
    );

    const dataErrorDm = data.filter(
      (i: any) =>
        i.formChange === "DOI_MOI" &&
        (String(i.serial) === String(i.attribute5) ||
          String(i.itemCode) === String(i.newItemCode)),
    );

    if (dataErrorDtd.length > 0) {
      NotificationExtension.Fails(
        `Serial đổi tương đương của ${dataErrorDtd.map((i: any) => i.serial).join(", ")} không được trùng với serial ban đầu !`,
      );
      return;
    }

    if (dataErrorDm.length > 0) {
      NotificationExtension.Fails(
        `Serial và mã sản phẩm của ${dataErrorDm.map((i: any) => i.serial).join(", ")} không được trùng với serial và mã sản phẩm ban đầu !`,
      );
      return;
    }

    handleConfirmInward();
  };

  const getDataItem = async (keySearch: any, serialChange?: string) => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      `/api/v1/TblItem/get-list?Take=25&KeySearch=${keySearch}`,
    );

    if (res && res?.success) {
      const result = res.data;
      setDataProduct(result);

      if (
        result.some(
          (i: any) => String(i.itemCode).trim() === String(keySearch).trim(),
        ) &&
        serialChange &&
        serialChange.length > 0
      ) {
        const dataChange = result.find(
          (i: any) => String(i.itemCode).trim() === String(keySearch).trim(),
        );

        handleChange(serialChange?.trim() || "", {
          attribute4: dataChange.itemId,
          newItemCode: dataChange.itemCode.trim(),
        });
      } else {
        handleChange(serialChange?.trim() || "", {
          attribute4: null,
          newItemCode: null,
        });
      }
    }
  };

  useEffect(() => {
    getDataItem("");
  }, []);

  useEffect(() => {
    setData(
      dataRowProps
        .filter(
          (value: any, index: any, self: any) =>
            index ===
            self.findIndex(
              (t: any) => JSON.stringify(t) === JSON.stringify(value),
            ),
        )
        .map((item: any) => ({
          ...item,
          formChange: item.formChange || "KHONG_DOI",
        })),
    );
  }, [dataRowProps]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  const table = useMantineReactTable({
    columns,
    data: data,
    positionToolbarAlertBanner: "bottom",
    enableTopToolbar: false,
    renderTopToolbarCustomActions: ({ table }) => <></>,
    renderToolbarInternalActions: ({ table }) => <></>,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select", "transactionNumber"],
        right: ["action"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    enablePagination: false,
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });

  return (
    <Box>
      <Box ref={headerRef}>
        <Group
          justify="space-between"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
          }}
        >
          <Badge
            variant="gradient"
            gradient={{ from: "blue", to: "cyan", deg: 90 }}
          >
            {isDetail
              ? `Chi tiết phiếu nhập kho bảo hành NCC${inwardInfo?.transactionCode ? ` - ${inwardInfo.transactionCode}` : ""}`
              : "Nhập kho bảo hành NCC"}
          </Badge>
          <Flex gap="md">
            <Button
              type="button"
              color="red"
              loading={visible}
              onClick={() => back(false)}
              leftSection={!visible ? <IconArrowBackUp size={18} /> : undefined}
            >
              Quay lại
            </Button>
            {!isDetail && (
              <Button
                color="teal"
                loading={visible}
                disabled={isSaved}
                leftSection={
                  !visible && !isSaved ? <IconCheck size={18} /> : undefined
                }
                onClick={() => handleSave()}
              >
                {isSaved ? "Đã lưu" : "Lưu"}
              </Button>
            )}
          </Flex>
        </Group>
        <Grid mt={5} gutter="md" mb="sm">
          {/* ===== CỘT TRÁI: THÔNG TIN BÊN XUẤT ===== */}
          <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Group
                gap="xs"
                mb="sm"
                pb="xs"
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                <ThemeIcon color="blue" variant="light" size="md" radius="sm">
                  <IconPackageExport size={18} />
                </ThemeIcon>
                <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                  Thông tin bên xuất
                </Text>
              </Group>
              <Grid gutter="xs">
                <Grid.Col span={12}>
                  <Select
                    label="Nhà cung cấp"
                    placeholder="Nhập tên NCC"
                    searchable
                    clearable
                    size="xs"
                    disabled={isDetail}
                    value={selectedVendorId}
                    data={dataVendorSelect || []}
                    nothingFoundMessage="Không tìm thấy dữ liệu !"
                    withAsterisk
                    onChange={(e) => {
                      setSelectedVendorId(e);
                      if (setTblItemTransactionHeaderCommand) {
                        setTblItemTransactionHeaderCommand((prev: any) => ({
                          ...prev,
                          attribute15: e ?? null, // mã NCC
                          customerId: e ? Number(e) : null,
                          customerName: e
                            ? getValueById(
                                e?.toString(),
                                dataVendorSelect,
                                "label",
                              )
                            : null,
                          telephoneNumber: e
                            ? getValueById(
                                e?.toString(),
                                dataVendorSelect,
                                "phone",
                              )
                            : null,
                          toAddress: e
                            ? getValueById(
                                e?.toString(),
                                dataVendorSelect,
                                "addr",
                              )
                            : null,
                        }));
                      }
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Textarea
                    label="Ghi chú"
                    placeholder="Nhập ghi chú"
                    size="xs"
                    minRows={5}
                    maxRows={5}
                    autosize
                    readOnly={isDetail}
                    variant={isDetail ? "filled" : "default"}
                    value={isDetail ? displayDescription : undefined}
                    onChange={(e) => {
                      if (setTblItemTransactionHeaderCommand) {
                        setTblItemTransactionHeaderCommand((prev: any) => ({
                          ...prev,
                          description: e.currentTarget.value,
                        }));
                      }
                    }}
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>

          {/* ===== CỘT GIỮA: THÔNG TIN BÊN NHẬN ===== */}
          <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Group
                gap="xs"
                mb="sm"
                pb="xs"
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                <ThemeIcon color="teal" variant="light" size="md" radius="sm">
                  <IconPackageImport size={18} />
                </ThemeIcon>
                <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                  Thông tin bên nhận
                </Text>
              </Group>
              <Grid gutter="xs">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Mã phiếu"
                    size="xs"
                    value={inwardInfo?.transactionCode || ""}
                    variant="filled"
                    readOnly
                    fw={600}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DatePickerInput
                    label="Ngày lập"
                    size="xs"
                    value={
                      inwardInfo?.createDate
                        ? new Date(inwardInfo.createDate)
                        : new Date()
                    }
                    valueFormat="DD-MM-YYYY"
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Chi nhánh"
                    size="xs"
                    value={
                      inwardInfo?.branchName ||
                      (firstItem?.invOrgCode && firstItem?.invOrgName
                        ? `${firstItem.invOrgCode} - ${firstItem.invOrgName}`
                        : firstItem?.invOrgCode) ||
                      ""
                    }
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Kho"
                    size="xs"
                    value={
                      firstItem?.subInvCode && firstItem?.subInvName
                        ? `${firstItem.subInvCode} - ${firstItem.subInvName}`
                        : firstItem?.subInvName || ""
                    }
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Tên người nhận"
                    size="xs"
                    value={inwardInfo?.creatorName || userLogin?.fullName || ""}
                    variant="filled"
                    readOnly
                    leftSection={<IconUser size={14} />}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Bộ phận"
                    size="xs"
                    value={
                      inwardInfo?.departmentName ||
                      userLogin?.departmentName ||
                      ""
                    }
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>

          {/* ===== CỘT PHẢI: THÔNG TIN KTKT ===== */}
          <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{
                height: "100%",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Group
                gap="xs"
                mb="sm"
                pb="xs"
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                <ThemeIcon color="orange" variant="light" size="md" radius="sm">
                  <IconUser size={18} />
                </ThemeIcon>
                <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                  Thông tin kỹ thuật (KTKT)
                </Text>
              </Group>
              <Grid gutter="xs">
                <Grid.Col span={12}>
                  <Select
                    label="Người kiểm tra"
                    placeholder="Chọn nhân viên"
                    searchable
                    clearable
                    size="xs"
                    withAsterisk
                    disabled={isDetail}
                    data={dataEmployee}
                    value={displayInspectorId}
                    onChange={(val) => {
                      if (setInspectorId) {
                        setInspectorId(val);
                      }
                    }}
                    nothingFoundMessage="Không tìm thấy nhân viên"
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Textarea
                    label="Ghi chú KTKT"
                    placeholder="Nhập ghi chú kỹ thuật"
                    size="xs"
                    minRows={5}
                    maxRows={5}
                    autosize
                    readOnly={isDetail}
                    variant={isDetail ? "filled" : "default"}
                    value={displayInspectorNote || ""}
                    onChange={(e) => {
                      if (setInspectorNote) {
                        setInspectorNote(e.currentTarget.value);
                      }
                    }}
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>
        </Grid>
      </Box>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default ServiceRepairInWardWarrantyVendor;
